import { z } from 'zod';
import { PaginationOrderDirectionEnum } from '@terra/shared-dto-types';
import {
  InternalNetworkAdEntityKindSchema,
  JsonValueSchema,
} from '@terra/shared-schemas-orchestrator';

export const AD_ENTITY_LIST_SORT_FIELDS = [
  'displayName',
  'samAccountName',
  'entityKind',
  'firstObservedAt',
  'isCrownJewel',
  'isOwned',
] as const;

export const AD_ENTITY_LIST_GROUP_BY_FIELDS = [
  'entityKind',
  'isCrownJewel',
  'isOwned',
] as const;

export const AdEntityListSortFieldSchema = z.enum(AD_ENTITY_LIST_SORT_FIELDS);

export const AdEntityListGroupByFieldSchema = z.enum(
  AD_ENTITY_LIST_GROUP_BY_FIELDS,
);

export const AdEntityListOrderBySchema = z.object({
  field: AdEntityListSortFieldSchema,
  order: PaginationOrderDirectionEnum,
});

export const AdEntityListGroupSchema = z.object({
  key: z.string().nullable(),
  count: z.number(),
});

const adEntityListOrderingFields = {
  orderBy: AdEntityListOrderBySchema.array().max(1).optional(),
  groupBy: AdEntityListGroupByFieldSchema.optional(),
};

export const InternalNetworkAdEntitySummarySchema = z.object({
  nodeId: z.string(),
  entityKind: InternalNetworkAdEntityKindSchema,
  objectId: z.string(),
  displayName: z.string(),
  samAccountName: z.string().nullable(),
  distinguishedName: z.string().nullable(),
  enabled: z.boolean().nullable().optional(),
  isCrownJewel: z.boolean(),
  isOwned: z.boolean(),
  firstObservedAt: z.iso.datetime().nullable().optional(),
});

export const InternalNetworkAdEntityDetailSchema =
  InternalNetworkAdEntitySummarySchema.extend({
    properties: JsonValueSchema.nullable(),
    canonicalAssetId: z.string().nullable(),
  });

export const ListInternalNetworkAdEntitiesQuerySchema = z.object({
  entityKind: InternalNetworkAdEntityKindSchema.optional(),
  limit: z.coerce.number().min(1).max(100).optional(),
  skip: z.coerce.number().min(0).max(100_000).optional(),
  firstObservedAfter: z.coerce.date().optional(),
  ...adEntityListOrderingFields,
});

export const ListInternalNetworkAdEntitiesResponseSchema = z.object({
  entities: z.array(InternalNetworkAdEntitySummarySchema),
  total: z.number(),
  groups: AdEntityListGroupSchema.array().optional(),
  sortApplied: z.boolean().optional(),
});

export const ListAllInternalNetworkAdEntitiesQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(100).optional(),
});

export const ListAllInternalNetworkAdEntitiesResponseSchema = z.object({
  entities: z.array(InternalNetworkAdEntitySummarySchema),
  totalsByKind: z.object(
    Object.fromEntries(
      InternalNetworkAdEntityKindSchema.options.map((kind) => [
        kind,
        z.number().optional(),
      ]),
    ) as Record<
      z.infer<typeof InternalNetworkAdEntityKindSchema>,
      z.ZodOptional<z.ZodNumber>
    >,
  ),
  truncated: z.boolean(),
});

export const SearchInternalNetworkAdEntitiesQuerySchema = z.object({
  q: z.string().min(1).max(200),
  entityKind: InternalNetworkAdEntityKindSchema.optional(),
  limit: z.coerce.number().min(1).max(100).optional(),
  skip: z.coerce.number().min(0).max(100_000).optional(),
  ...adEntityListOrderingFields,
});

export const SearchInternalNetworkAdEntitiesResponseSchema =
  ListInternalNetworkAdEntitiesResponseSchema;

export const LookupInternalNetworkAdEntitiesByObjectIdsBodySchema = z.object({
  objectIds: z.array(z.string().min(1)).min(1).max(25),
});

export const LookupInternalNetworkAdEntitiesByObjectIdsResponseSchema =
  ListInternalNetworkAdEntitiesResponseSchema;

export type AdEntityListSortField = z.infer<typeof AdEntityListSortFieldSchema>;
export type AdEntityListGroupByField = z.infer<
  typeof AdEntityListGroupByFieldSchema
>;
export type AdEntityListOrderByDto = z.infer<typeof AdEntityListOrderBySchema>;
export type AdEntityListGroupDto = z.infer<typeof AdEntityListGroupSchema>;
export type InternalNetworkAdEntitySummaryDto = z.infer<
  typeof InternalNetworkAdEntitySummarySchema
>;
export type InternalNetworkAdEntityDetailDto = z.infer<
  typeof InternalNetworkAdEntityDetailSchema
>;
export type ListInternalNetworkAdEntitiesQueryDto = z.infer<
  typeof ListInternalNetworkAdEntitiesQuerySchema
>;
export type ListInternalNetworkAdEntitiesResponseDto = z.infer<
  typeof ListInternalNetworkAdEntitiesResponseSchema
>;
export type SearchInternalNetworkAdEntitiesQueryDto = z.infer<
  typeof SearchInternalNetworkAdEntitiesQuerySchema
>;
export type LookupInternalNetworkAdEntitiesByObjectIdsBodyDto = z.infer<
  typeof LookupInternalNetworkAdEntitiesByObjectIdsBodySchema
>;
